import { useState, useEffect, useRef, useCallback } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { DownloadIcon, PlusIcon } from '@heroicons/react/solid';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import { Table } from '@wajeez-web/components/data-table';
import { useParams } from 'react-router-dom';
import { convertMarkdownToHtml } from '@wajeez-web/core/utils';
import { useAppDispatch } from '@wajeez-web/stores/base';
import {
  QuizzesActions,
  QuestionsActions,
  AnswersActions,
  contentEntityDataType,
} from '@wajeez-web/stores/content';
import { useContentSelector } from '@wajeez-web/stores/content';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { Server } from '@wajeez-web/core/api-base';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { CSVLink } from 'react-csv';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';

import QuizzesModal, { Input as QuizInput } from '../components/QuizzesModal';
import {
  QuestionsModal,
  Input as QuestionInput,
} from '../components/QuestionsModal';
import {
  ShowQuestionsModal,
  Input as ShowQuestionsInput,
} from '../components/ShowQuestionsModal';
import { DataTableWrapper } from '@wajeez-web/components/helpers';

const QuizzesPage = () => {
  const { id } = useParams<{ type: string; id: string }>();

  const dispatch = useAppDispatch();

  const exportDataRef = useRef<any>(null);

  const [quizModalState, setQuizModalState] = useState<{
    open: boolean;
    data?: QuizInput;
  }>({ open: false, data: undefined });
  const [questionModalState, setQuestionModalState] = useState<{
    open: boolean;
    data: QuestionInput | undefined;
  }>({ open: false, data: undefined });
  const [showQuestionsModalState, setShowQuestionsModalState] = useState<{
    open: boolean;
    data: QuestionInput | undefined;
  }>({ open: false, data: undefined });
  const [isGetQuestionsFulfilled, setIsGetQuestionsFulfilled] = useState(false);
  const [isAddAnswersFulfilled, setIsAddAnswersFulfilled] = useState(false);
  const [addedQuestion, setAddedQuestion] = useState<
    Server.QuestionDashboardOutput | undefined
  >();

  const { dataTable, create, update } = useContentSelector(
    (state) => state.quizzes
  );

  const questions = useContentSelector(({ questions }) => questions.lookups);
  const orderQuestions = useContentSelector(({ questions }) => questions.order);

  const answers = useContentSelector(({ answers }) => answers.lookups);

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData(
    'quizzes',
    QuizzesActions.getQuizzesAsDataTableAsync,
    undefined,
    { contentEntityId: id },
    [{ property: 32, orderByDirection: 1 }]
  );

  const loadData = useCallback(() => {
    dispatch(
      QuizzesActions.getQuizzesAsDataTableAsync({ contentEntityId: id })
    );
  }, [dispatch, id]);

  const onExport = async () => {
    if (dataTable.data && tablePreferances) {
      exportDataRef.current.link.click();
    }
  };

  const getQuestions = async (quizId: number) => {
    return dispatch(QuestionsActions.getQuestionsByQuizIdAsync(quizId));
  };

  const getAnswers = useCallback(
    async (questionId: number) => {
      return dispatch(AnswersActions.getAnswersByQuestionIdAsync(questionId));
    },
    [dispatch]
  );

  const getExportedData = () => {
    const d = [];
    const dataTableData = dataTable.data;

    if (dataTableData?.totalCount && dataTableData?.totalCount > 0) {
      for (let i = 0; i < dataTableData?.totalCount; i++) {
        const row: { [key: string]: any } = {};

        dataTableData?.['columns']?.forEach((item: any) => {
          if (!item.isHidden) {
            row[item.header] = item.cells[i];
          }
        });

        d.push(row);
      }
    }

    return d;
  };

  const addAnswers = useCallback(async () => {
    if (isGetQuestionsFulfilled) {
      const addedQuestion = questions.data.find(
        (q) =>
          // q.quizId === questionModalState?.data?.quizId &&
          q.text === questionModalState?.data?.text
      );

      if (addedQuestion && questionModalState?.data?.answers?.length) {
        setAddedQuestion(addedQuestion);

        const addAnswersResponses = [];
        let isAllResponsesOk = true;

        for (let i = 0; i < questionModalState?.data?.answers?.length; i++) {
          addAnswersResponses.push(
            await dispatch(
              AnswersActions.createNewAnswerAsync({
                // @ts-ignore
                languageId: 1,
                questionId: addedQuestion.id,
                text: questionModalState?.data.answers?.[i].text,
              })
            )
          );
        }

        for (const s of addAnswersResponses) {
          if (s.meta.requestStatus !== 'fulfilled') {
            isAllResponsesOk = false;

            break;
          }
        }

        if (isAllResponsesOk && addedQuestion.id) {
          await getAnswers(addedQuestion.id);

          setIsAddAnswersFulfilled(true);
        }
      }

      setIsGetQuestionsFulfilled(false);
    }
  }, [
    dispatch,
    isGetQuestionsFulfilled,
    questionModalState,
    questions,
    getAnswers,
  ]);

  const setCorrectAnswer = useCallback(async () => {
    if (
      isAddAnswersFulfilled &&
      addedQuestion?.id &&
      answers[addedQuestion?.id]
    ) {
      const correctAnswer = questionModalState?.data?.answers?.find(
        (a) => a.isCorrect
      );

      const setCorrectAnswerResponse = await dispatch(
        AnswersActions.setCorrectAnswerAsync({
          questionId: addedQuestion?.id,
          answerId: answers[addedQuestion?.id].data?.find(
            (a) => a.text === correctAnswer?.text
          )?.id,
        })
      );

      if (setCorrectAnswerResponse?.meta.requestStatus === 'fulfilled') {
        setQuestionModalState({ open: false, data: undefined });

        dispatch(
          CommonActions.setAlert({
            type: AlertType.SUCCESS,
            messages: [
              {
                message: 'تمت العملية بنجاح',
                code: 200,
                field: '',
              },
            ],
            for: 'page',
          })
        );
      }

      setIsAddAnswersFulfilled(false);
    }
  }, [
    isAddAnswersFulfilled,
    addedQuestion,
    answers,
    dispatch,
    questionModalState,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    addAnswers();
  }, [addAnswers]);

  useEffect(() => {
    setCorrectAnswer();
  }, [setCorrectAnswer]);

  return (
    <>
      <CSVLink
        filename={'Quizzes.csv'}
        data={getExportedData()}
        ref={exportDataRef}
        className="hidden"
      >
        Download me
      </CSVLink>
      <PageLayoutsContainer
        title={`Quizzes`}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add Quiz'}
                Icon={PlusIcon}
                onClick={() => {
                  setQuizModalState({ open: true });
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [AdminSystemPermissions.quizzes.add],
          },
        ]}
        breadCrumbsLinks={[{ title: '' }]}
      >
        <DataTableWrapper
          data={dataTable.data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          isLoading={dataTable.status === 'loading'}
          rowActions={(row) => {
            return [
              {
                title: 'Edit',
                endpoint: '',
                permission: AdminSystemPermissions.quizzes.edit,
                type: 'request',
                action: () => {
                  setQuizModalState({
                    open: true,
                    data: {
                      id: Number(row[1]),
                      name: row[4] as string,
                      passingGrade: Number(row[64]),
                    },
                  });
                },
              },
              // {
              //   title: 'Add Question',
              //   endpoint: '',
              //   permission: AdminSystemPermissions.questions.add,
              //   type: 'request',
              //   action: () => {
              //     setQuestionModalState({
              //       open: true,
              //       data: {
              //         quizId: Number(row[1]),
              //       },
              //     });
              //   },
              // },
              // {
              //   title: 'Show Questions',
              //   endpoint: '',
              //   permission: AdminSystemPermissions.questions.lookupsByQuizId,
              //   type: 'request',
              //   action: () => {
              //     setShowQuestionsModalState({
              //       open: true,
              //       data: {
              //         quizId: Number(row[1]),
              //       },
              //     });
              //   },
              // },
              // {
              //   title: row[8] === 'False' ? 'Publish Quiz' : 'Unpublish Quiz',
              //   endpoint: '',
              //   permission: AdminSystemPermissions.quizzes.publish,
              //   type: 'request',
              //   action: async () => {
              //     const res = await dispatch(
              //       QuizzesActions.changeQuizPublishStateAsync({
              //         id: Number(row[1]),
              //         publish: row[8] === 'False' ? true : false,
              //       })
              //     );

              //     if (res.meta.requestStatus === 'fulfilled') {
              //       loadData();
              //     }
              //   },
              // },
              // {
              //   title: 'Content',
              //   permission: AdminSystemPermissions.authors.content,
              //   endpoint: ``,
              //   type: 'request',
              //   action: () => {
              //     navigate(`${row[1]}/content`);
              //   },
              // },
            ];
          }}
          columnsSelectors={(r) => {
            return {
              '4': (
                <Button
                  type="text"
                  color="primary"
                  title={r[4]}
                  onClick={() => {
                    setQuizModalState({
                      open: true,
                      data: {
                        id: Number(r[1]),
                        name: r[4] as string,
                        passingGrade: Number(r[2]),
                      },
                    });
                  }}
                />
              ),
              '16': (
                <div
                  dangerouslySetInnerHTML={{
                    __html: convertMarkdownToHtml(r[16] as string)! || '',
                  }}
                />
              ),
            };
          }}
          extraActions={[
            {
              label: 'Export as CSV',
              onClick: () => {
                onExport();
              },
              Icon: <DownloadIcon className="h-4 w-4" />,
            },
          ]}
          preferences={tablePreferances || undefined}
          setPreferences={setTablePreferences}
        />
      </PageLayoutsContainer>
      {quizModalState.open && (
        <QuizzesModal
          data={
            quizModalState.data &&
            ({
              name: quizModalState.data?.name,
              passingGrade: quizModalState.data?.passingGrade,
            } as QuizInput)
          }
          isOpen={quizModalState.open}
          onClose={() => setQuizModalState({ open: false, data: undefined })}
          onSubmit={async (data) => {
            if (data) {
              const res = quizModalState.data
                ? await dispatch(
                    QuizzesActions.editQuizAsync({
                      id: quizModalState.data.id,
                      name: data.name,
                      passingGrade: data.passingGrade,
                    })
                  )
                : await dispatch(
                    QuizzesActions.createNewQuizAsync({
                      contentEntityId: id as string,
                      name: data.name,
                      passingGrade: data.passingGrade,
                    })
                  );

              if (res.meta.requestStatus === 'fulfilled') {
                getData();

                setQuizModalState({ open: false, data: undefined });

                dispatch(
                  CommonActions.setAlert({
                    type: AlertType.SUCCESS,
                    messages: [
                      { message: 'تمت العملية بنجاح', code: 200, field: '' },
                    ],
                    for: 'page',
                  })
                );
              }
            }
          }}
        />
      )}
      {/* {questionModalState.open && (
        <QuestionsModal
          data={
            questionModalState.data &&
            ({
              quizId: questionModalState.data.quizId,
            } as QuestionInput)
          }
          isOpen={questionModalState.open}
          onClose={() =>
            setQuestionModalState({ open: false, data: undefined })
          }
          onSubmit={async (data) => {
            // if (data) {
            //   const res = await dispatch(
            //     QuestionsActions.createNewQuestionAsync({
            //       // @ts-ignore
            //       languageId: 1,
            //       quizId: data.quizId,
            //       text: data.question,
            //     })
            //   );
            //   if (res.meta.requestStatus === 'fulfilled') {
            //     const questionsResponse = await getQuestions(data.quizId);
            //     if (questionsResponse.meta.requestStatus === 'fulfilled') {
            //       setIsGetQuestionsFulfilled(true);
            //       setQuestionModalState({
            //         ...questionModalState,
            //         data: { ...data, quizId: Number(data.quizId) },
            //       });
            //     }
            //   }
            // }
          }}
        />
      )} */}
      {/* {showQuestionsModalState.open && (
        <ShowQuestionsModal
          data={
            showQuestionsModalState.data &&
            ({
              quizId: showQuestionsModalState.data?.quizId,
              questions: questions.data,
            } as ShowQuestionsInput)
          }
          isOpen={showQuestionsModalState.open}
          onClose={() =>
            setShowQuestionsModalState({ open: false, data: undefined })
          }
          onSubmit={async (input) => {
            const res =
              input &&
              (await dispatch(
                QuestionsActions.orderQuestionsAsync({
                  quizId: showQuestionsModalState.data?.quizId,
                  questionIds: input.questions.map((q) => q.id) as number[],
                })
              ));

            if (res?.meta.requestStatus === 'fulfilled') {
              setShowQuestionsModalState({ open: false, data: undefined });
            }
          }}
          loading={orderQuestions.status === 'loading'}
        />
      )} */}
    </>
  );
};

export default QuizzesPage;
